/* Decides which subscription tier applies, in two places: on the PDP as the shopper picks a
   quantity, and in the cart as lines come and go. Also owns the copy that explains the offer,
   on the PDP and in the cart, so a single tally drives both the price and what it is called.

   The 40% rate is a reward for cart VOLUME, but Shopify selling plans attach to a variant, not
   to a cart. So the variant carries both tiers (in Skio, the small size ticked into the 40%
   group as well as the 20% one) and this decides which one each line should be on:

     units at that cadence >= threshold  ->  those lines move to the best-priced plan
     units at that cadence <  threshold  ->  those lines move back to the base-priced plan

   Counted PER CADENCE, because each cadence is a separate subscription: 12 bars monthly plus
   12 bars every two months is two 12-bar orders, not one 24-bar order.

   Counted PER FAMILY, because the families have different thresholds and different units --
   see snippets/bar-promo-config.liquid, which is the only place any of that is written down.
   Minis and Protein bars both use "24 Bars" variant titles, so a title alone can never decide
   what a line counts toward; the handle picks the family first.

   Safe to ship before the Skio change: with only one tier allocated there is never a different
   plan to move to, so every pass is a no-op.

   NOT enforcement. A crafted add-to-cart URL can still put a single small pack on a 40% plan;
   only a Shopify Function closes that. */
(function () {
  if (window.__krfBarPricing) return;
  window.__krfBarPricing = true;

  var productCache = {};

  function config() {
    var el = document.querySelector('[data-krf-volume-promo]');
    if (!el) return null;
    try {
      var programs = (JSON.parse(el.textContent).programs || []).filter(function (p) {
        return p && p.handles && p.handles.length;
      });
      return programs.length ? programs : null;
    } catch (e) {
      return null;
    }
  }

  function programFor(programs, handle) {
    for (var i = 0; i < programs.length; i++) {
      if (programs[i].handles.indexOf(handle) !== -1) return programs[i];
    }
    return null;
  }

  /* "12 Bars" -> 12, "Peanut Butter Cup Granola / 2 Bags" -> 2. Granola is a two-option
     product, so the size can sit in either half of the title. The unit comes from the family,
     which is why a granola line can never be read as a bar count. */
  function unitsIn(variantTitle, unit) {
    var parts = String(variantTitle || '').split('/');
    var re = new RegExp('^(\\d+)\\s+' + unit + 's?$', 'i');
    for (var i = 0; i < parts.length; i++) {
      var m = re.exec(parts[i].trim());
      if (m) return parseInt(m[1], 10);
    }
    return 0;
  }

  function plural(noun, n) {
    return n === 1 ? noun : noun + 's';
  }

  /* One-time units count toward the threshold as well as subscription units -- a shopper
     buying 24 bars has bought 24 bars however they pay for them. They cannot RECEIVE the 40%
     themselves (that price is a selling plan, which only exists on a subscription line), but
     they do help the subscription lines earn it.

     Subscription units are still tallied per cadence, because each cadence is its own
     subscription. One-time units sit in a shared pool that counts toward every cadence: with
     two cadences in one cart they are effectively counted twice, which is deliberate -- the
     shopper really did buy them once and both subscriptions really are part of that order. */
  function tally(cart, programs) {
    var totals = {};
    cart.items.forEach(function (i) {
      var program = programFor(programs, i.handle);
      if (!program) return;
      var units = unitsIn(i.variant_title, program.unit) * i.quantity;
      if (!units) return;

      var bucket = totals[program.id] ||
                   (totals[program.id] = { byCadence: {}, oneTime: 0, total: 0 });
      bucket.total += units;
      if (i.selling_plan_allocation) {
        var key = cadenceOf(i.selling_plan_allocation.selling_plan);
        bucket.byCadence[key] = (bucket.byCadence[key] || 0) + units;
      } else {
        bucket.oneTime += units;
      }
    });
    return totals;
  }

  function getProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
    return productCache[handle];
  }

  /* Allocations carry only a plan id; the cadence lives on the plan itself, in the groups. */
  function planIndex(product) {
    var byId = {};
    (product.selling_plan_groups || []).forEach(function (g) {
      (g.selling_plans || []).forEach(function (p) { byId[p.id] = p; });
    });
    return byId;
  }

  /* The two tiers label the same cadence differently -- the 20% plans are named
     "every month" / "every 2 months", the 40% plans "Every Month" / "Every 2 Months".
     Comparing the raw strings silently prevented every upgrade, so reduce both to a
     canonical key: lowercase, drop the leading "every", singularise the unit.
       "every month"    -> "month"
       "Every Month"    -> "month"
       "every 2 months" -> "2 month"
       "Every 2 Months" -> "2 month" */
  function cadenceOf(plan) {
    if (!plan) return '';
    var raw = (plan.options && plan.options.length && plan.options[0].value) || plan.name || '';
    return String(raw)
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^every\s+/, '')
      .replace(/s$/, '');
  }

  /* The allocation this line should be on: the shopper's own cadence, at the tier their cart
     has earned. Returns the one it is already on when there is nothing else to choose from --
     the caller still needs its price to label the line. */
  function targetAllocationFor(item, product, wantBest) {
    var variant = variantOf(item, product);
    if (!variant) return null;

    var byId = planIndex(product);
    var wantedCadence = cadenceOf(item.selling_plan_allocation && item.selling_plan_allocation.selling_plan);

    var sameCadence = (variant.selling_plan_allocations || []).filter(function (a) {
      return cadenceOf(byId[a.selling_plan_id]) === wantedCadence;
    });
    if (!sameCadence.length) return null;

    var sorted = sameCadence.slice().sort(function (a, b) { return a.price - b.price; });
    return wantBest ? sorted[0] : sorted[sorted.length - 1];
  }

  function variantOf(item, product) {
    return (product.variants || []).filter(function (v) { return v.id === item.variant_id; })[0];
  }

  /* The Discount property is a snapshot the PDP takes at add-to-cart, and moving a line to
     another tier leaves it behind -- checkout then reads "Discount: 20%" beside a line that is
     being charged 40%. Returns the line's properties with that one corrected, or null when
     there is nothing to correct.

     Only ever corrected, never introduced: a line that never carried the property has nothing
     to be wrong about, and inventing one would put a label on someone else's line item. */
  function correctedProperties(item, product, allocation) {
    var current = item.properties && item.properties.Discount;
    if (!current) return null;

    var variant = variantOf(item, product);
    if (!variant || !variant.price) return null;

    var percent = Math.round((variant.price - allocation.price) / variant.price * 100) + '%';
    if (String(current) === percent) return null;

    var props = {};
    Object.keys(item.properties).forEach(function (k) { props[k] = item.properties[k]; });
    props.Discount = percent;
    return props;
  }

  /* KRF 2026-10-06: the shopper's own cart changes. component-cart-items.js marks <html> with
     data-krf-cart-busy while one is in flight and bumps data-krf-cart-seq each time one starts.
     A plan move built from an older /cart.js would resend that snapshot's quantity and undo the
     shopper's change, and an older render would bring back a row they just removed. */
  function shopperBusy() {
    return document.documentElement.hasAttribute('data-krf-cart-busy');
  }

  function shopperSeq() {
    return document.documentElement.getAttribute('data-krf-cart-seq');
  }

  function refreshCartSections() {
    var comps = Array.prototype.slice.call(document.querySelectorAll('cart-items-component[data-section-id]'));
    if (!comps.length) return Promise.resolve();
    var url = window.location.pathname.indexOf('/cart') === 0 ? '/cart' : '/';
    var seq = shopperSeq();
    return Promise.all(comps.map(function (comp) {
      return fetch(url + '?section_id=' + encodeURIComponent(comp.dataset.sectionId))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html')
            .querySelector('cart-items-component[data-section-id="' + comp.dataset.sectionId + '"]');
          if (fresh && !shopperBusy() && shopperSeq() === seq) comp.replaceWith(fresh);
        })
        .catch(function () {});
    }));
  }

  function notifyCart(cart) {
    return import('@theme/events')
      .catch(function () { return {}; })
      .then(function (mod) {
        if (mod && mod.CartUpdateEvent) {
          document.dispatchEvent(new mod.CartUpdateEvent(cart, '', {
            itemCount: cart.item_count,
            source: 'bar-promo-pricing'
          }));
        }
      });
  }

  var chain = Promise.resolve();

  function reconcile() {
    chain = chain.then(function () {
      var programs = config();
      if (!programs) return;
      if (shopperBusy()) return schedule(500);
      var seq = shopperSeq();

      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var totals = tally(cart, programs);
          paint(programs, totals);

          var lines = [];
          cart.items.forEach(function (item) {
            if (!item.selling_plan_allocation) return;
            var program = programFor(programs, item.handle);
            if (!program || !unitsIn(item.variant_title, program.unit)) return;
            lines.push({ item: item, program: program });
          });
          if (!lines.length) return;

          return Promise.all(lines.map(function (l) { return getProduct(l.item.handle); }))
            .then(function (products) {
              var changes = [];
              lines.forEach(function (line, idx) {
                var product = products[idx];
                if (!product) return;

                var item = line.item;
                var bucket = totals[line.program.id];
                var key = cadenceOf(item.selling_plan_allocation.selling_plan);
                var reached = (bucket.byCadence[key] || 0) + bucket.oneTime;

                var allocation = targetAllocationFor(item, product,
                  reached >= line.program.threshold);
                if (!allocation) return;

                var movingPlan = allocation.selling_plan_id !== item.selling_plan_allocation.selling_plan.id;
                var properties = correctedProperties(item, product, allocation);
                if (!movingPlan && !properties) return;

                changes.push({
                  key: item.key,
                  quantity: item.quantity,
                  plan: allocation.selling_plan_id,
                  properties: properties
                });
              });
              if (!changes.length) return;

              /* Sequential on purpose: /cart/change.js rewrites the line list, and parallel
                 calls race each other into 422s. */
              setBusy(CART, true);
              var run = Promise.resolve();
              var stale = false;
              changes.forEach(function (c) {
                run = run.then(function (prev) {
                  /* Checked before every POST: c.quantity is from the snapshot, so a shopper
                     change since then means stop, and re-read the cart on the next pass. */
                  if (stale || shopperBusy() || shopperSeq() !== seq) {
                    stale = true;
                    return prev;
                  }
                  return fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    /* properties is left out unless there is a correction: sending it at all
                       REPLACES the line's whole property set. */
                    body: JSON.stringify({
                      id: c.key,
                      quantity: c.quantity,
                      selling_plan: c.plan,
                      properties: c.properties || undefined
                    })
                  }).then(function (r) { return r.ok ? r.json() : null; });
                });
              });

              return run
                .then(function (updated) {
                  if (stale) return null;
                  return updated || fetch('/cart.js').then(function (r) { return r.json(); });
                })
                .then(function (updated) {
                  /* The shopper's own response re-renders the cart. */
                  if (stale) return schedule(500);
                  return notifyCart(updated).then(function () { return refreshCartSections(); });
                })
                .then(function () { setBusy(CART, false); }, function () { setBusy(CART, false); });
            });
        });
    }).then(function () { setBusy(CART, false); }, function () { setBusy(CART, false); });
    return chain;
  }

  var timer = null;
  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay);
  }

  document.addEventListener('cart:update', function (e) {
    if (e && e.detail && e.detail.source === 'bar-promo-pricing') return; // our own echo
    schedule(250);
  });
  document.addEventListener('cart-drawer:open', function () { schedule(0); });

  setInterval(function () { if (!document.hidden) reconcile(); }, 5000);

  /* ---- copy -----------------------------------------------------------------------------
     Kept here rather than in its own script because every sentence is a statement about the
     tally, and two scripts counting the cart separately is how the two end up disagreeing. */

  var lastPrograms = null;
  var lastTotals = null;

  function paint(programs, totals) {
    lastPrograms = programs;
    lastTotals = totals;
    paintCartNote(programs, totals);
    paintOffers(programs, totals);
  }

  function repaint() {
    if (lastPrograms) paint(lastPrograms, lastTotals);
  }

  /* The note speaks per cadence, because that is how the discount is actually decided. With
     a single cadence it reads naturally; with two it names the one that still falls short
     rather than quoting a total the shopper cannot map to anything. A cart can be short in
     one family and done in another, so each family gets its own line. */
  function paintCartNote(programs, totals) {
    var notes = Array.prototype.slice.call(document.querySelectorAll('[data-krf-bar-note]'));
    if (!notes.length) return;

    var messages = [];
    programs.forEach(function (program) {
      var bucket = totals[program.id];
      if (!bucket) return;

      var reached = {};
      Object.keys(bucket.byCadence).forEach(function (k) {
        reached[k] = bucket.byCadence[k] + bucket.oneTime;
      });

      var keys = Object.keys(reached);
      if (!keys.length) return; // one-time only: no subscription line to discount

      var short = keys.filter(function (k) {
        return reached[k] < program.threshold;
      });
      if (!short.length) {
        messages.push({
          earned: true,
          text: '✓ 40% off applied to your subscription ' + plural(program.noun, 2) + '.'
        });
        return;
      }

      var worst = short.sort(function (a, b) { return reached[b] - reached[a]; })[0];
      var missing = program.threshold - reached[worst];
      messages.push({
        earned: false,
        text: 'Add ' + missing + ' more ' + plural(program.noun, missing) +
              (keys.length > 1 ? ' to your ' + worst.replace(/^(\d)/, 'every $1') + ' delivery' : '') +
              ' to get 40% off your subscription.'
      });
    });

    var key = JSON.stringify(messages);
    notes.forEach(function (note) {
      if (note.getAttribute('data-krf-note-key') !== key) {
        note.setAttribute('data-krf-note-key', key);
        note.textContent = '';
        messages.forEach(function (m) {
          var line = document.createElement('p');
          line.className = 'krf-bar-note__line' + (m.earned ? ' krf-bar-note__line--earned' : '');
          line.textContent = m.text;
          note.appendChild(line);
        });
      }
      note.hidden = !messages.length;
    });
  }

  /* The PDP prompts ("order at least N...") are asking for something the cart may already
     have. Hidden once it does, on the family's own total -- one-time lines included, since
     they count toward the threshold too. */
  function paintOffers(programs, totals) {
    var offers = Array.prototype.slice.call(document.querySelectorAll('[data-krf-bar-offer]'));
    if (!offers.length) return;

    var byId = {};
    programs.forEach(function (p) { byId[p.id] = p; });

    offers.forEach(function (el) {
      var program = byId[el.getAttribute('data-krf-program')];
      if (!program) return;
      var bucket = totals[program.id];
      el.hidden = !!bucket && bucket.total >= program.threshold;
    });
  }

  /* ---- busy state ---------------------------------------------------------------------
     Every path here costs a round trip -- /cart/change, a section re-render, a Skio
     re-render on variant change -- and without a signal the shopper sees stale prices and
     clicks again, which is how double-adds happen. Scoped to the block being rebuilt rather
     than a page-wide overlay. */
  var busySince = {};

  function paintBusy(selector, state) {
    Array.prototype.forEach.call(document.querySelectorAll(selector), function (el) {
      el.classList.toggle('krf-busy', !!state);
      if (state) { el.setAttribute('aria-busy', 'true'); } else { el.removeAttribute('aria-busy'); }
    });
  }

  /* A round trip can finish in 40ms, and a spinner that appears and vanishes inside one frame
     reads as a glitch rather than as feedback. Hold it for a minimum beat so the shopper sees
     that something happened. */
  var MIN_BUSY_MS = 400;

  function setBusy(selector, state) {
    if (state) {
      busySince[selector] = Date.now();
      paintBusy(selector, true);
      return;
    }
    var elapsed = Date.now() - (busySince[selector] || 0);
    var wait = Math.max(0, MIN_BUSY_MS - elapsed);
    setTimeout(function () { paintBusy(selector, false); }, wait);
  }

  var PICKER = '.skio-plan-picker, [data-skio-plan-picker], .skio-group-container';
  var CART = 'cart-items-component, [data-krf-bar-note]';

  /* ---- PDP: reflect the tier the shopper is actually about to buy -------------------
     Liquid renders the picker for quantity 1, so a shopper setting quantity 2 on a 12-pack
     saw "Save 20% / $31.99" and only discovered the 40% once the line reached the cart.
     Here the selected quantity is folded in, and the form is updated so Add to Cart posts
     the correct plan -- the cart then arrives already right and the reconciler above has
     nothing to correct.

     Everything is driven from the variant's own allocations, so a variant with a single
     tier (any product whose small size is not ticked in Skio yet) is left alone. */
  function pdpSync() {
    var programs = config();
    if (!programs) return Promise.resolve();

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return Promise.resolve();

    var select = form.querySelector('.skio-frequency');
    var qtyEl = form.querySelector('[name="quantity"]');
    var idEl = form.querySelector('[name="id"]');
    if (!select || !qtyEl || !idEl) return Promise.resolve();

    var handle = (window.location.pathname.split('/products/')[1] || '').split('?')[0].split('/')[0];
    var program = programFor(programs, handle);
    if (!program) return Promise.resolve();

    /* Which variant is on screen, and it is NOT the form's hidden [name="id"]. The theme
       updates the size pills immediately and the picker re-renders from the server, but the
       form's id lags both by a few hundred milliseconds. Reading it mid-switch prices the
       OUTGOING variant and paints that over the incoming picker -- which is exactly the
       "2 Bags at the 1 Bag price" that showed up on the live site. The picker carries the
       variant the server rendered it for, so ask the markup, not the form. */
    var picker = document.querySelector('[skio-plan-picker][data-skio-variant]');
    var rendered = picker && picker.getAttribute('data-skio-variant');

    return getProduct(handle).then(function (product) {
      if (!product) return;
      var variantId = parseInt(rendered || idEl.value, 10);
      var variant = (product.variants || []).filter(function (v) { return v.id === variantId; })[0];
      if (!variant) return;

      var allocations = variant.selling_plan_allocations || [];
      var prices = allocations.map(function (a) { return a.price; }).sort(function (a, b) { return a - b; });
      if (prices.length < 2 || prices[0] === prices[prices.length - 1]) return; // one tier only

      var byId = planIndex(product);

      /* Deliberately blind to the cart: this box prices what is on this page, so a shopper
         reading "Save 40%" beside a single 12-pack can take it at face value. The cart is
         where units from anywhere are added up, and the reconciler upgrades the line there. */
      var qty = parseInt(qtyEl.value, 10) || 1;
      var units = unitsIn(variant.title, program.unit) * qty;

      var targetPrice = units >= program.threshold ? prices[0] : prices[prices.length - 1];

      /* Rewrite each cadence option to point at the target tier, keeping the shopper's
         chosen cadence exactly where it was. */
      Array.prototype.forEach.call(select.options, function (opt) {
        var cadence = cadenceOf(byId[parseInt(opt.value, 10)]);
        var match = allocations.filter(function (a) {
          return a.price === targetPrice && cadenceOf(byId[a.selling_plan_id]) === cadence;
        })[0];
        if (match) opt.value = String(match.selling_plan_id);
      });

      var percent = Math.round((variant.price - targetPrice) / variant.price * 100);

      /* The picker renders prices, multiplying this unit price by the quantity. Writing the
         text here instead would be overwritten on its next render -- and compounded by it. */
      var priceEl = document.querySelector('[skio-subscription-price]');
      if (priceEl && priceEl.getAttribute('data-krf-unit') !== String(targetPrice)) {
        priceEl.setAttribute('data-krf-unit', targetPrice);
      }

      var discountEl = document.querySelector('[skio-discount]');
      if (discountEl) discountEl.textContent = percent + '%';

      var discountProp = form.querySelector('[name="properties[Discount]"]');
      if (discountProp) discountProp.value = percent + '%';

      /* Only follow the select when a plan is actually selected -- an empty value means the
         shopper is on One-time purchase and Skio owns that field. */
      var planEl = form.querySelector('[name="selling_plan"]');
      if (planEl && planEl.value) planEl.value = select.value;
    });
  }

  var pdpTimer = null;
  function schedulePdp() {
    setBusy(PICKER, true);
    clearTimeout(pdpTimer);
    pdpTimer = setTimeout(function () {
      var done = function () {
        holdSubscriptionPrice(false);
        setBusy(PICKER, false);
      };
      pdpSync().then(done, done);
    }, 120);
  }

  ['input', 'change'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[name="quantity"], .skio-frequency, .skio-group-input, variant-picker')) {
        schedulePdp();
      }
    }, true);
  });

  /* The quantity decides the tier, and the picker announces every change to it -- including
     Horizon's +/- buttons, which assign input.value directly and fire no events of their own.
     The subscription price is held for the round trip: the picker would otherwise multiply the
     outgoing unit price and flash a number that is about to be replaced. */
  document.addEventListener('krf:quantity', function () {
    holdSubscriptionPrice(true);
    schedulePdp();
  });

  function holdSubscriptionPrice(state) {
    var el = document.querySelector('[skio-subscription-price]');
    if (!el) return;
    if (state) { el.setAttribute('data-krf-hold', ''); } else { el.removeAttribute('data-krf-hold'); }
  }

  /* Cart quantity controls do their own round trip before cart:update fires, so show the
     cart as busy from the click and let the reconcile pass clear it. */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('cart-items-component')) {
      var control = e.target.closest('button, [name="minus"], [name="plus"], quantity-selector-component');
      if (control) setBusy(CART, true);
    }
  }, true);

  /* The picker is re-rendered from the server on variant change, which resets the option
     values Liquid wrote for quantity 1 and recreates the offer copy with nothing to announce
     it -- both have to be reapplied to the new markup. */
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if ((node.matches && node.matches('.skio-frequency, [data-krf-bar-offer]')) ||
            (node.querySelector && node.querySelector('.skio-frequency, [data-krf-bar-offer]'))) {
          repaint();
          return schedulePdp();
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { schedule(0); schedulePdp(); }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { schedule(0); schedulePdp(); });
  } else {
    schedule(0);
    schedulePdp();
  }
})();
